/**
 * 激励广告四项整份配置表单，默认配置与语言覆盖共用字段和校验路径。
 */
import type { RewardedAdConfig } from '@novel-admin/shared';
import { FormRow } from '~/ui/FormRow';

export type RewardedAdConfigDraft = Record<keyof RewardedAdConfig, string>;
export type RewardedAdFieldErrors = Record<string, string>;

const CONFIG_FIELDS = [
  {
    key: 'bonus_reward',
    label: '每次任务奖励 BONUS',
    min: 1,
    max: 100,
    hint: '1–100；仅任务中心奖励',
  },
  {
    key: 'task_daily_limit',
    label: '任务每日广告名额',
    min: 0,
    max: 10,
    hint: '0–10；0 关闭新任务入口',
  },
  {
    key: 'chapter_daily_limit',
    label: '每日广告解锁章节上限',
    min: 0,
    max: 10,
    hint: '0–10；0 关闭新章节广告及授予；每次 AD 解锁固定消费两次章节广告次数',
  },
  {
    key: 'registration_day_threshold',
    label: '注册日龄门槛',
    min: 0,
    max: 10,
    hint: '0–10；注册当地日期为 D0，默认 D2 起可经每日判定首次授予 IAA 功能',
  },
] as const;

export function rewardedAdConfigToDraft(config: RewardedAdConfig): RewardedAdConfigDraft {
  return Object.fromEntries(
    CONFIG_FIELDS.map(({ key }) => [key, String(config[key])]),
  ) as RewardedAdConfigDraft;
}

export function rewardedAdConfigFromDraft(draft: RewardedAdConfigDraft): RewardedAdConfig {
  // 空输入不能被 Number('') 静默转换成合法的 0。
  return Object.fromEntries(
    CONFIG_FIELDS.map(({ key }) => [
      key,
      draft[key].trim() === '' ? Number.NaN : Number(draft[key]),
    ]),
  ) as RewardedAdConfig;
}

export function RewardedAdConfigFields({
  value,
  onChange,
  disabled = false,
  errors = {},
  prefix = 'config',
}: {
  value: RewardedAdConfigDraft;
  onChange: (value: RewardedAdConfigDraft) => void;
  disabled?: boolean;
  errors?: RewardedAdFieldErrors;
  prefix?: string;
}) {
  return (
    <>
      {CONFIG_FIELDS.map(({ key, label, min, max, hint }) => {
        const error = errors[`${prefix}.${key}`];
        return (
          <FormRow key={key} label={label} hint={hint} required>
            <input
              type="number"
              className="input mono"
              aria-label={label}
              min={min}
              max={max}
              step={1}
              value={value[key]}
              disabled={disabled}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? `${prefix}-${key}-error` : undefined}
              onChange={(event) => onChange({ ...value, [key]: event.target.value })}
              style={{ maxWidth: 160 }}
            />
            {error && (
              <div
                id={`${prefix}-${key}-error`}
                role="alert"
                style={{ color: 'var(--neg)', fontSize: 12 }}
              >
                {error}
              </div>
            )}
          </FormRow>
        );
      })}
    </>
  );
}
